"use client";

import React from "react";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/shadcn-components/ui/resizable";
import HeaderDesktop from "./header/HeaderDesktop";
import ViewDesktop from "./view/ViewDesktop";
import FooterDesktop from "./footer/FooterDesktop";
import { AgentSidebarMockup } from "./agent";
import { useFixedPixelPanel } from "./hooks/useFixedPixelPanel";
import { DESKTOP_LAYOUT_STORAGE_KEYS } from "@/lib/localStorageKeys/desktop-layout";
import { useDesktopLayoutStore } from "@/lib/desktop-layout/store";
import { cn } from "@/lib/utils";
import VybeStudioDialog from "@/components/chart/chart-indicators/custom/VybeStudioDialog";
import { useVybeStudioStore } from "@/lib/custom-indicators/studio-store";
import { useIsAdmin } from "@/lib/vybe-auth";

interface DesktopScreenProps {
  // Props for future desktop-specific functionality
}

const FOOTER_DEFAULT_PX = 260;
const FOOTER_MIN_PX = 140;
const VIEW_MIN_PERCENT = 30;

const AGENT_SIDEBAR_DEFAULT_PX = 360;
const AGENT_SIDEBAR_MIN_PX = 280;
const MAIN_AREA_MIN_PERCENT = 40;

export default function DesktopScreen({}: DesktopScreenProps) {
  // Vybe Studio is admin-only (client-side UX gating, not a security boundary).
  const isAdmin = useIsAdmin();
  const studioOpen = useVybeStudioStore((s) => s.open);
  const setStudioOpen = useVybeStudioStore((s) => s.setOpen);

  const footerVisible = useDesktopLayoutStore((s) => s.footerVisible);
  const agentSidebarVisible = useDesktopLayoutStore(
    (s) => s.agentSidebarVisible,
  );

  const {
    containerRef: verticalContainerRef,
    groupRef: verticalGroupRef,
    initialPercent: footerInitialPercent,
    controlledMinPercent: footerControlledMinPercent,
    handleLayout: handleFooterLayout,
    handleDragging: handleFooterDragging,
  } = useFixedPixelPanel({
    storageKey: DESKTOP_LAYOUT_STORAGE_KEYS.FOOTER_HEIGHT_PX,
    defaultPx: FOOTER_DEFAULT_PX,
    minPx: FOOTER_MIN_PX,
    direction: "vertical",
    controlledIndex: 1,
    otherMinPercent: VIEW_MIN_PERCENT,
    isCollapsed: !footerVisible,
  });

  const {
    containerRef: agentContainerRef,
    groupRef: agentGroupRef,
    initialPercent: agentInitialPercent,
    controlledMinPercent: agentControlledMinPercent,
    handleLayout: handleAgentLayout,
    handleDragging: handleAgentDragging,
  } = useFixedPixelPanel({
    storageKey: DESKTOP_LAYOUT_STORAGE_KEYS.AGENT_SIDEBAR_WIDTH_PX,
    defaultPx: AGENT_SIDEBAR_DEFAULT_PX,
    minPx: AGENT_SIDEBAR_MIN_PX,
    direction: "horizontal",
    controlledIndex: 1,
    otherMinPercent: MAIN_AREA_MIN_PERCENT,
    isCollapsed: !agentSidebarVisible,
  });

  return (
    <div className="flex h-dvh w-full max-w-full flex-col overflow-hidden bg-background text-foreground">
      <HeaderDesktop />

      {isAdmin && (
        <VybeStudioDialog
          open={studioOpen}
          onOpenChange={setStudioOpen}
          isDesktop
        />
      )}

      <div ref={agentContainerRef} className="flex-1 overflow-hidden">
        <ResizablePanelGroup
          ref={agentGroupRef}
          direction="horizontal"
          className="h-full"
          onLayout={handleAgentLayout}
        >
          <ResizablePanel
            defaultSize={100 - agentInitialPercent}
            minSize={MAIN_AREA_MIN_PERCENT}
          >
            <div
              ref={verticalContainerRef}
              className="flex-1 h-full overflow-hidden"
            >
              <ResizablePanelGroup
                ref={verticalGroupRef}
                direction="vertical"
                className="h-full"
                onLayout={handleFooterLayout}
              >
                <ResizablePanel
                  className="flex min-h-0 min-w-0 flex-col overflow-hidden"
                  defaultSize={100 - footerInitialPercent}
                  minSize={VIEW_MIN_PERCENT}
                >
                  <ViewDesktop />
                </ResizablePanel>

                <ResizableHandle
                  onDragging={handleFooterDragging}
                  className={cn(!footerVisible && "hidden")}
                />

                <ResizablePanel
                  defaultSize={footerInitialPercent}
                  minSize={footerControlledMinPercent}
                >
                  {footerVisible ? <FooterDesktop /> : null}
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
          </ResizablePanel>

          <ResizableHandle
            onDragging={handleAgentDragging}
            className={cn(!agentSidebarVisible && "hidden")}
          />

          <ResizablePanel
            defaultSize={agentInitialPercent}
            minSize={agentControlledMinPercent}
          >
            {agentSidebarVisible ? <AgentSidebarMockup /> : null}
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
}
